/**
 * Dialog — wrapper sobre @radix-ui/react-dialog com tokens do DS.
 * Radix entrega focus trap, ESC, ARIA e backdrop click; reusamos.
 */

import {
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Close,
} from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { cn } from "@/lib/utils";

export const Dialog = Root;
export const DialogTrigger = Trigger;
export const DialogClose = Close;

export function DialogContent({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Content> & { children: ReactNode }) {
  return (
    <Portal>
      <Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in" />
      <Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2",
          "rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-2xl",
          "focus:outline-none",
          className,
        )}
        {...props}
      >
        {children}
        <Close
          aria-label="Fechar"
          className="absolute right-3 top-3 rounded-md p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-secondary)] hover:text-[var(--color-foreground)]"
        >
          <X className="h-4 w-4" />
        </Close>
      </Content>
    </Portal>
  );
}

export function DialogTitle({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Title>) {
  return (
    <Title
      className={cn("text-lg font-semibold tracking-tight", className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Description>) {
  return (
    <Description
      className={cn("mt-1 text-sm text-[var(--color-muted-foreground)]", className)}
      {...props}
    />
  );
}
